import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Button, ButtonPreset } from "shared/Button";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import AngleInputIcon from "shared/assets/Angle_Input.svg";
import YesNoToggle from "../YesNoToggle";
import ColorDropdown from "../ColorDropdown";
import styles from "./ThumbnailColorPicker.module.css";

const BRAND_COLORS = [
  { label: "Light Blue", value: "#91B6BB" },
  { label: "Slate Grey", value: "#415364" },
  { label: "Dark Blue", value: "#243746" },
  { label: "Grey", value: "#ADB7C0" },
  { label: "Tan", value: "#A89C96" },
  { label: "Brown", value: "#9E703C" },
  { label: "White", value: "#FFFFFF" },
  { label: "Black", value: "#000000" },
];

const ORDINAL_LABELS = ["1st", "2nd", "3rd"];

function normalizeHex(value) {
  if (!value) {
    return "";
  }

  const normalized = value.trim().toUpperCase();
  if (/^#([0-9A-F]{6}|[0-9A-F]{8})$/.test(normalized)) {
    return normalized;
  }

  return "";
}

export function ThumbnailColorPicker({
  useIconColor,
  onUseIconColorChange,
  color,
  onColorChange,
  useGradient,
  onUseGradientChange,
  gradient,
  onGradientChange,
  className,
}) {
  const isUsingIconColor = useIconColor !== false;
  const isUsingGradient = useGradient === true;
  const normalizedCustomColor = normalizeHex(color);
  const normalizedGradient = {
    angle: Number.isFinite(Number(gradient?.angle))
      ? Number(gradient.angle)
      : 40,
    colors: [
      normalizeHex(gradient?.colors?.[0]) || "#FFFFFF",
      normalizeHex(gradient?.colors?.[1]) || "#243746",
      normalizeHex(gradient?.colors?.[2]) || "",
    ],
  };

  const [isSolidOpen, setIsSolidOpen] = React.useState(false);
  const [isFirstGradientOpen, setIsFirstGradientOpen] = React.useState(false);
  const [isSecondGradientOpen, setIsSecondGradientOpen] = React.useState(false);
  const [isThirdGradientOpen, setIsThirdGradientOpen] = React.useState(false);

  const closeAllDropdowns = () => {
    setIsSolidOpen(false);
    setIsFirstGradientOpen(false);
    setIsSecondGradientOpen(false);
    setIsThirdGradientOpen(false);
  };

  const handleDropdownToggle = (name, nextOpen) => {
    closeAllDropdowns();

    if (!nextOpen) {
      return;
    }

    if (name === "solid") setIsSolidOpen(true);
    if (name === "first") setIsFirstGradientOpen(true);
    if (name === "second") setIsSecondGradientOpen(true);
    if (name === "third") setIsThirdGradientOpen(true);
  };

  const handleGradientColorChange = (index, nextColor) => {
    const nextColors = [...normalizedGradient.colors];
    nextColors[index] = normalizeHex(nextColor) || nextColor.toUpperCase();

    onGradientChange({
      ...normalizedGradient,
      colors: nextColors,
    });
  };

  const updateAngle = (nextAngle) => {
    onGradientChange({
      ...normalizedGradient,
      angle: Math.max(
        0,
        Math.min(360, Number.isFinite(nextAngle) ? nextAngle : 0),
      ),
    });
  };

  return (
    <section
      className={classNames(styles.card, className, {
        [styles.cardWithGradient]: isUsingGradient,
      })}
    >
      <div className={styles.rowHeader}>
        <h3 className={styles.sectionTitle}>Color</h3>
        <div className={styles.divider} />
      </div>

      <div className={styles.colorBody}>
        <YesNoToggle
          label="Use Icon Color"
          value={isUsingIconColor}
          onChange={onUseIconColorChange}
        />

        {!isUsingIconColor && !isUsingGradient ? (
          <div className={styles.gradientRow}>
            <span className={styles.inputLabel}>Color</span>
            <ColorDropdown
              options={BRAND_COLORS}
              selectedOption={normalizedCustomColor || "#FFFFFF"}
              onChange={(value) => onColorChange(value)}
              isOpen={isSolidOpen}
              onToggle={(nextOpen) => handleDropdownToggle("solid", nextOpen)}
            />
          </div>
        ) : null}

        <YesNoToggle
          label="Use Gradient"
          value={isUsingGradient}
          onChange={onUseGradientChange}
        />

        {isUsingGradient ? (
          <div className={styles.gradientControls}>
            <div className={classNames(styles.gradientRow, styles.angleRow)}>
              <label htmlFor="gradient-angle" className={styles.inputLabel}>
                Angle
              </label>
              <div className={styles.angleInput}>
                <input
                  id="gradient-angle"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={styles.angleValue}
                  value={normalizedGradient.angle}
                  onChange={(event) => {
                    const digitsOnly = event.target.value.replace(/\D/g, "");
                    updateAngle(digitsOnly === "" ? 0 : Number(digitsOnly));
                  }}
                />
                <div className={styles.angleDivider} />
                <div className={styles.angleArrowSlot}>
                  <img
                    src={AngleInputIcon}
                    alt=""
                    className={styles.angleArrowShell}
                  />
                  <div className={styles.angleArrowBox}>
                    <button
                      type="button"
                      className={styles.angleArrowButton}
                      aria-label="Increase angle"
                      onClick={() => updateAngle(normalizedGradient.angle + 1)}
                    />
                    <button
                      type="button"
                      className={styles.angleArrowButton}
                      aria-label="Decrease angle"
                      onClick={() => updateAngle(normalizedGradient.angle - 1)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {[0, 1].map((index) => (
              <div key={index} className={styles.gradientRow}>
                <span className={styles.inputLabel}>
                  {ORDINAL_LABELS[index]} Color
                </span>
                <ColorDropdown
                  options={BRAND_COLORS}
                  selectedOption={normalizedGradient.colors[index]}
                  onChange={(value) => handleGradientColorChange(index, value)}
                  isOpen={
                    index === 0 ? isFirstGradientOpen : isSecondGradientOpen
                  }
                  onToggle={(nextOpen) =>
                    handleDropdownToggle(
                      index === 0 ? "first" : "second",
                      nextOpen,
                    )
                  }
                />
              </div>
            ))}

            {normalizedGradient.colors[2] ? (
              <div className={styles.gradientRow}>
                <span className={styles.inputLabel}>3rd Color</span>
                <ColorDropdown
                  options={BRAND_COLORS}
                  selectedOption={normalizedGradient.colors[2]}
                  onChange={(value) => handleGradientColorChange(2, value)}
                  isOpen={isThirdGradientOpen}
                  onToggle={(nextOpen) =>
                    handleDropdownToggle("third", nextOpen)
                  }
                />
              </div>
            ) : (
              <div className={styles.gradientRow}>
                <span className={styles.inputLabel}>3rd Color</span>
                <Button
                  preset={ButtonPreset.addWithIcon}
                  icon={<img src={PlusSquareIcon} alt="" />}
                  onClick={() => {
                    const nextColors = [...normalizedGradient.colors];
                    nextColors[2] = nextColors[1] || "#A89C96";
                    onGradientChange({
                      ...normalizedGradient,
                      colors: nextColors,
                    });
                    handleDropdownToggle("third", true);
                  }}
                >
                  Add
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

ThumbnailColorPicker.propTypes = {
  useIconColor: PropTypes.bool,
  onUseIconColorChange: PropTypes.func,
  color: PropTypes.string,
  onColorChange: PropTypes.func,
  useGradient: PropTypes.bool,
  onUseGradientChange: PropTypes.func,
  gradient: PropTypes.shape({
    angle: PropTypes.number,
    colors: PropTypes.arrayOf(PropTypes.string),
  }),
  onGradientChange: PropTypes.func,
  className: PropTypes.string,
};

ThumbnailColorPicker.defaultProps = {
  useIconColor: true,
  onUseIconColorChange: () => {},
  color: "#FFFFFF",
  onColorChange: () => {},
  useGradient: false,
  onUseGradientChange: () => {},
  gradient: {
    angle: 40,
    colors: ["#FFFFFF", "#243746", ""],
  },
  onGradientChange: () => {},
  className: undefined,
};

export default ThumbnailColorPicker;
